import React, { useState, useEffect } from 'react';
import { Spin, Result, Select, Modal } from "antd";
import { LoadingOutlined, SearchOutlined, CheckCircleOutlined, ClockCircleOutlined, MailOutlined } from "@ant-design/icons";
import axiosInstance from '../config/axios-instance';
import { MyNotif } from "../components/NotifIcons/NotifIcons";
import "../components/AllClients/AllClient.scss";
import Head from "../components/Head";
import { HrIcon } from "../components/product-3d-config-tabs/forms/canvas/icon/HrIcon";

interface RequestInfo {
  id: number;
  product_id: number;
  client_id: number;
  message: string;
  can_see: number;
  created_at: string;
  updated_at: string;
  product?: {
    id: number;
    code: string;
    name: string;
  };
  client?: {
    id: number;
    name: string;
    email: string;
  };
}

const { Option } = Select;

const RequestInformationList: React.FC = () => {
  const [requests, setRequests] = useState<RequestInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'seen'>('all');
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchRequests();
  }, [filter]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axiosInstance({
        method: 'get',
        url: '/products/list-request-information',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      console.log(response.data);
      const responseData = response.data;
      if (responseData.data && Array.isArray(responseData.data)) {
        setRequests(responseData.data);
      } else if (Array.isArray(responseData)) {
        setRequests(responseData);
      } else {
        setRequests([]);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsSeen = async (id: number) => {
    try {
      const token = localStorage.getItem('token');
      await axiosInstance({
        method: 'put',
        url: `/products/request-information/${id}/mark-seen`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      MyNotif('Request marked as seen', 'success');
      fetchRequests();
    } catch (error) {
      console.error('Error marking as seen:', error);
      MyNotif('Error marking as seen', 'danger');
    }
  };

  const safeRequests = Array.isArray(requests) ? requests : [];
  
  const filteredRequests = safeRequests.filter((req) => {
    const statusMatch = (() => {
      if (filter === 'pending') return Number(req.can_see) === 0;
      if (filter === 'seen') return Number(req.can_see) === 1;
      return true;
    })();

    const searchLower = searchTerm.toLowerCase();
    const searchMatch = 
      req.client?.name.toLowerCase().includes(searchLower) ||
      req.product?.name.toLowerCase().includes(searchLower) ||
      req.product?.code.toLowerCase().includes(searchLower);

    return statusMatch && (searchTerm === "" ? true : searchMatch);
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleSearchChange = (event: any) => {
    setSearchTerm(event.target.value);
  };

  return (
    <>
      {loading ? (
        <div
          className="loading_orders"
          style={{ height: "50vh", width: "50vw", margin: "0 auto" }}>
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
          />
        </div>
      ) : (
        <div className="my_orders">
          <Head title="Product Information Requests" />
          <div className="container">
            <div className="d-flex align-items-center justify-content-between pr-1 mb-1">
               <div>
                  <span className="mb-0 mt-3 list_of_companies_title">Request Information</span>
               </div>
            </div>

            <div className="row mt-4">
              <div className="col-12 col-md-8">
                 <div className="search-bar searchfaq" style={{width: "100%"}}>
                    <div className="input-container">
                      <input 
                        type="text" 
                        placeholder="Search by client, product name or code..." 
                        onChange={handleSearchChange}
                        className={"search_input select_add_product"}
                      />
                      <HrIcon></HrIcon>
                      <button type="submit" className={"submit_input"}><SearchOutlined/></button>
                    </div>
                  </div>
              </div>
              <div className="col-12 col-md-4">
                <Select
                  defaultValue="all"
                  style={{ width: "100%" }}
                  className="select_add_product"
                  onChange={(value: any) => setFilter(value)}
                  size="large"
                >
                  <Option value="all">All Requests</Option>
                  <Option value="pending">Pending</Option>
                  <Option value="seen">Seen</Option>
                </Select>
              </div>
            </div>

            {filteredRequests.length > 0 ? (
              <div style={{ overflowY: "auto", marginTop: "20px" }}>
                <table className="table table_list_ofproduct">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Client</th>
                      <th>Product</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRequests.map((req) => (
                      <tr key={req.id}>
                        <td>
                          <p className="mb-0">#{req.id}</p>
                        </td>
                        <td>
                          <p className="mb-0 font-weight-bold">{req.client?.name || 'N/A'}</p>
                          <p className="mb-0 text-muted" style={{fontSize: '12px'}}>{req.client?.email}</p>
                        </td>
                        <td>
                          <p className="mb-0">{req.product?.code || 'N/A'}</p>
                          <p className="mb-0 text-muted" style={{fontSize: '12px'}}>{req.product?.name}</p>
                        </td>
                     
                        <td>
                           <p className="mb-0">
                             {Number(req.can_see) === 0 ? (
                               <span className="text-warning"><ClockCircleOutlined /> Pending</span>
                             ) : (
                               <span className="text-success"><CheckCircleOutlined /> Seen</span>
                             )}
                           </p>
                        </td>
                        <td>
                           {Number(req.can_see) === 0 && (
                             <button
                               onClick={() => markAsSeen(req.id)}
                               className="button_member"
                               title="Mark as Seen"
                             >
                               <CheckCircleOutlined style={{ fontSize: "20px", color: "#00468c" }} />
                             </button>
                           )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Result
                status="404"
                title="No Requests"
                subTitle="There are no requests matching your criteria."
              />
            )}
          </div>


        </div>
      )}
    </>
  );
};

export default RequestInformationList;